/**
 * GeoDefinition
 * -------------
 * Kompakter, elegant eingebetteter Definitionsblock für GEO / AI Search Readiness.
 *
 * - Eine semantisch klare „Was ist …?"-Definition (kurz, voice-search-tauglich).
 * - Optional 2–4 Q&A-Paare für ergänzende KI-Verständlichkeit.
 * - Bewusst zurückhaltend gestaltet, damit der bestehende Funnel-Look erhalten bleibt.
 *
 * Visuelles wird über das schon bestehende Dark-Theme + PINE-Green Akzent gesteuert.
 */
export default function GeoDefinition({
  term,
  shortTitle,
  definition,
  qa = [],
  overline = "Definition",
  variant = "default",
}) {
  const bg = variant === "muted" ? "bg-[#0B0F10]" : "";
  const heading = shortTitle || `Was ist ${term}?`;
  const slug = (term || "general")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ß/g, "ss")
    .replace(/[_\s]+/g, "-");

  return (
    <section
      className={`border-t border-[rgba(255,255,255,0.08)] ${bg}`}
      data-testid={`geo-definition-${slug}`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="overline mb-4">{overline}</div>
            <h2
              className="font-display text-2xl md:text-3xl uppercase leading-[1.1]"
              itemProp="name"
            >
              {heading}
            </h2>
          </div>
          <div className="lg:col-span-7">
            <p
              className="text-[#AEB7B7] text-base md:text-lg leading-relaxed border-l-2 border-[#18E096] pl-5"
              itemProp="description"
            >
              {definition}
            </p>

            {qa.length > 0 && (
              <dl className="mt-8 divide-y divide-[rgba(255,255,255,0.08)] border-t border-b border-[rgba(255,255,255,0.08)]">
                {qa.map((row, i) => (
                  <div key={i} className="py-5">
                    <dt className="text-white text-sm md:text-base font-medium leading-snug">
                      {row.q}
                    </dt>
                    <dd className="text-[#AEB7B7] text-sm md:text-base mt-2 leading-relaxed">
                      {row.a}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Helper: erzeugt ein schema.org DefinedTerm-Objekt für JSON-LD-Embedding.
 */
export function buildDefinedTerm({ name, description, url, alternateName }) {
  return {
    "@context": "https://schema.org",
    "@type": "DefinedTerm",
    name,
    description,
    inDefinedTermSet: "https://www.pinexs.de/systeme",
    ...(alternateName ? { alternateName } : {}),
    ...(url ? { url } : {}),
  };
}

/**
 * Helper: FAQPage-Builder aus einfacher [{q,a}] Liste.
 */
export function buildFaqJsonLd(qa) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: qa.map((r) => ({
      "@type": "Question",
      name: r.q,
      acceptedAnswer: { "@type": "Answer", text: r.a },
    })),
  };
}
